import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

/**
 * The soft pause. When the backend answers 402 we do not show an error and we never show a
 * token count, we say capacity is used up, promise their work is intact, and offer the next plan.
 * Mount once, at app level.
 */
export default function CreditGate() {
  const [state, setState] = useState(null);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      r => r,
      err => {
        if (err?.response?.status === 402) {
          const d = err.response.data?.detail || {};
          setState({ message: d.message, needed: d.needed, available: d.available });
        }
        return Promise.reject(err);
      });
    return () => axios.interceptors.response.eject(id);
  }, []);

  return (
    <AnimatePresence>
      {state && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-[#0a1729]/80 backdrop-blur-sm"
          data-testid="credit-gate">
          <motion.div initial={{ opacity: 0, y: 18, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
            className="relative w-full max-w-lg glass-tint glass-cyan fp-rail p-7">
            <button onClick={() => setState(null)} data-testid="credit-gate-close"
              className="absolute top-4 right-4 text-slate-300 hover:text-white transition-colors">
              <X className="w-5 h-5" />
            </button>

            <Sparkles className="w-7 h-7 text-cyan-300" />
            <h2 className="font-outfit text-2xl font-semibold text-white mt-4">
              You've used your AI capacity for this cycle
            </h2>
            <p className="text-base text-slate-200/90 mt-3 leading-relaxed">
              {state.message
                || "Everything you've created is still here. Choose a plan to keep generating personalized FuturePath intelligence."}
            </p>

            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/pricing" onClick={() => setState(null)} data-testid="credit-gate-upgrade">
                <Button className="rounded-full bg-gradient-to-r from-cyan-500 to-violet-500 text-slate-900 font-semibold">
                  See plans <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
              <Button variant="ghost" onClick={() => setState(null)} data-testid="credit-gate-dismiss"
                className="rounded-full text-slate-300">
                Keep looking around
              </Button>
            </div>
            <p className="text-xs text-slate-300 mt-5">
              Nothing is deleted, and everything you've already generated stays readable.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
